import * as _ from 'underscore'

import { Enums } from '../common/enums'

import { CommonHelper } from '../../helpers/common-helper'
import { DataProvider, Dictionary } from '../../helpers'

import { OpDataType } from './op-data-type'
import { OpViewColumnData } from './op-view-column-data'
import { ObjectCustomString } from '../util/object-custom-string'

import { Utils } from '../util/utils'
import { OpDataList } from '../util/op-data-list'
import { IOpObject, IOpChangeState, IReferenceType } from '../util/interfaces'

export class OpViewColumn implements IOpObject, IOpChangeState, IReferenceType {
    // #region Properties

    public idViewColumn: number
    public idView: number
    public idDataType: number
    public name: string
    public table: string
    public primaryKey: boolean
    public defaultValue: any
    public size: number
    public idViewColumnType: number
    public idViewColumnSource: number
    public key: boolean
    public idKeyViewColumn: number

    // #endregion

    // #region NavigationProperties

    public dataType: OpDataType

    // #endregion

    // #region CustomProperties

    public get caption() {
        if (this.dataType != null) {
            return this.dataType.toString()
        }

        return this.name
    }

    public get indexNbr() {
        const split: string[] = CommonHelper.splitStringWithStringSplitOptions(this.name, '_', '')

        if (split.length > 1 && angular.isNumber(split[split.length - 1])) {
            return +split[split.length - 1]
        }

        return 0
    }

    public get shouldSkipTranslation(): boolean {
        return this.dataList == null ? false : !!this.dataList.getValue(Enums.DataType.VIEW_COLUMN_SKIP_TRANSLATION);
    }

    public get isHidden(): boolean {
        if (this.dataList == null) {
            return null
        }

        const data: any = this.dataList.getValue(Enums.DataType.VIEW_COLUMN_IS_HIDDEN)

        if (data == null) {
            return null
        }

        return !!data
    }

    // #endregion

    // #region Ctor

    constructor(opObj: any = null) {
        this.fromJSON(opObj)
    }

    // #endregion

    // #region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    // #endregion

    // #region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdViewColumn
    }

    public getReferenceKey(): number {
        return this.idViewColumn
    }

    public getReferenceValue(): any {
        return this
    }

    // #endregion

    // #region IOpObject Implementation

    public get idObject(): any {
        return this.idViewColumn
    }

    public set idObject(idViewColumn: any) {
        this.idViewColumn = idViewColumn
    }

    public dataList: OpDataList<OpViewColumnData> = new OpDataList<OpViewColumnData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string


    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idViewColumn = opObj.ID_VIEW_COLUMN
            this.idView = opObj.ID_VIEW
            this.idDataType = opObj.ID_DATA_TYPE
            this.name = opObj.NAME
            this.table = opObj.TABLE
            this.primaryKey = opObj.PRIMARY_KEY
            this.defaultValue = opObj.DEFAULT_VALUE
            this.size = opObj.SIZE
            this.idViewColumnSource = opObj.ID_VIEW_COLUMN_SOURCE
            this.idViewColumnType = opObj.ID_VIEW_COLUMN_TYPE
            this.key = opObj.KEY
            this.idKeyViewColumn = opObj.ID_KEY_VIEW_COLUMN
            this.opState = opObj.OpState

            this.dataType = opObj._DataType ? new OpDataType(opObj._DataType) : null

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpViewColumnData(data)))
            }
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_VIEW_COLUMN: this.idViewColumn,
            ID_VIEW: this.idView,
            ID_DATA_TYPE: this.idDataType,
            NAME: this.name,
            TABLE: this.table,
            PRIMARY_KEY: this.primaryKey,
            DEFAULT_VALUE: this.defaultValue,
            SIZE: this.size,
            ID_VIEW_COLUMN_SOURCE: this.idViewColumnSource,
            ID_VIEW_COLUMN_TYPE: this.idViewColumnType,
            KEY: this.key,
            ID_KEY_VIEW_COLUMN: this.idKeyViewColumn,

            DataList: this.dataList.ToJSON(),

            OpState: this.opState
        }
    }

    public toString() {
        const customFormatString: string = ObjectCustomString.getCustomStringFormat(this)

        if (customFormatString == null) {
            return this.name
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)

    }

    // #endregion

    // #region Static

    public static restore(restoredOpObj: OpViewColumn) {
        if (restoredOpObj) {
            const opObj: OpViewColumn = new OpViewColumn()

            opObj.idViewColumn = restoredOpObj.idViewColumn
            opObj.idView = restoredOpObj.idView
            opObj.idDataType = restoredOpObj.idDataType
            opObj.name = restoredOpObj.name
            opObj.table = restoredOpObj.table
            opObj.primaryKey = restoredOpObj.primaryKey
            opObj.defaultValue = restoredOpObj.defaultValue
            opObj.size = restoredOpObj.size
            opObj.idViewColumnSource = restoredOpObj.idViewColumnSource
            opObj.idViewColumnType = restoredOpObj.idViewColumnType
            opObj.key = restoredOpObj.key
            opObj.idKeyViewColumn = restoredOpObj.idKeyViewColumn
            opObj.opState = restoredOpObj.opState
            opObj.dataType = restoredOpObj.dataType ? OpDataType.restore(restoredOpObj.dataType) : null

            return opObj
        }
        return null
    }

    public static getColumnIdDataType(columnName: string) {
        const split: string[] = columnName.replace("[", "").replace("]", "").split('_').filter(v => !Utils.isNullOrEmpty(v))

        if (split.length == 1) {
            return +split[0]
        }

        return +split[split.length - 2]
    }

    // #endregion

    // #region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpViewColumn[]): JQueryPromise<OpViewColumn[]> {
        return Utils
            .loadDataListNavigationProperties(dataProvider, list)
            .then(() => {
                return dataProvider.System
                    .getDictionaries([Enums.ReferenceType.IdDataType])
                    .then((dict: Dictionary<Dictionary<IReferenceType>>) => {
                        let dataTypeDict: Dictionary<OpDataType> = new Dictionary<OpDataType>(null)

                        if (dict.containsKey(Enums.ReferenceType.IdDataType)) {
                            dataTypeDict = <Dictionary<OpDataType>>dict.tryGetValue(Enums.ReferenceType.IdDataType)
                        }

                        _.forEach(list, (viewColumn: OpViewColumn) => {
                            viewColumn.dataType = dataTypeDict != null ? dataTypeDict.tryGetValue(viewColumn.idDataType) : null
                        })

                        return list
                    })
            })
    }

    // #endregion
}
